import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, MapPin, Calendar, Gauge } from "lucide-react";
import Reveal from "@/components/Reveal";
import { IMAGES } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const PROJECT_META = [
  { id: "ete-condominio", tag: "ambiental", image: IMAGES.eteEta, span: "lg:col-span-7" },
  { id: "chorume-aterro", tag: "chorume", image: IMAGES.chorume, span: "lg:col-span-5" },
  { id: "usina-solar", tag: "fotovoltaica", image: IMAGES.fotovoltaica, span: "lg:col-span-5" },
  { id: "steel-frame", tag: "construcao-seca", image: IMAGES.steelFrame, span: "lg:col-span-7" },
  { id: "eta-modular", tag: "ambiental", image: IMAGES.heroBg, span: "lg:col-span-6" },
  { id: "retrofit-industrial", tag: "construcao-seca", image: IMAGES.reformas, span: "lg:col-span-6" },
];

const Projects = () => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState("todos");
  const [active, setActive] = useState(null);

  const projects = PROJECT_META.map((meta) => ({
    ...meta,
    ...t.projects.items.find((p) => p.id === meta.id),
  }));
  const visible = filter === "todos" ? projects : projects.filter((p) => p.tag === filter);

  return (
    <section id="projetos" data-testid="projects-section" className="bg-[#F8FAFC] py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 sm:px-10 lg:px-16">
        <Reveal>
          <p className="font-mono-tech text-xs uppercase tracking-[0.35em] text-emerald-600">
            {t.projects.eyebrow}
          </p>
          <h2 className="font-display mt-4 max-w-2xl text-3xl font-bold tracking-tight text-[#07120C] sm:text-4xl lg:text-5xl">
            {t.projects.title}
          </h2>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600">
            {t.projects.lead}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-2.5" data-testid="project-filters">
            {t.projects.filters.map((f) => (
              <button
                key={f.id}
                data-testid={`project-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`font-mono-tech rounded-full px-5 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                  filter === f.id
                    ? "bg-emerald-600 text-white"
                    : "border border-slate-300 bg-white text-slate-600 hover:border-emerald-500 hover:text-emerald-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((project) => (
              <motion.button
                layout
                key={project.id}
                type="button"
                data-testid={`project-card-${project.id}`}
                onClick={() => setActive(project)}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative h-72 overflow-hidden rounded-3xl text-left md:h-80 md:col-span-1 lg:col-span-6 ${project.span}`}
              >
                <img
                  src={project.image}
                  alt={project.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#07120C]/90 via-[#07120C]/25 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="font-mono-tech rounded-full bg-emerald-500/90 px-3.5 py-1 text-[10px] uppercase tracking-[0.2em] text-[#07120C]">
                    {project.tagLabel}
                  </span>
                  <h3 className="font-display mt-3 text-lg font-bold leading-snug text-white sm:text-xl">
                    {project.title}
                  </h3>
                  <p className="font-mono-tech mt-2 flex items-center gap-2 text-xs tracking-[0.15em] text-emerald-300/90">
                    <Gauge className="h-3.5 w-3.5" /> {project.capacity}
                  </p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="project-modal-overlay"
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-[#07120C]/90 backdrop-blur-sm"
              onClick={() => setActive(null)}
            />
            <motion.div
              data-testid="project-modal"
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-emerald-500/25 bg-[#0F2117]"
            >
              <div className="relative h-56 sm:h-72">
                <img src={active.image} alt={active.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F2117] to-transparent" />
                <button
                  data-testid="project-modal-close"
                  onClick={() => setActive(null)}
                  className="absolute right-4 top-4 rounded-full border border-emerald-500/30 bg-[#07120C]/70 p-2 text-emerald-300 backdrop-blur-md transition-colors hover:bg-[#07120C] hover:text-emerald-100"
                  aria-label={t.projects.close}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="p-7 sm:p-9">
                <span className="font-mono-tech rounded-full bg-emerald-500/15 px-3.5 py-1 text-[10px] uppercase tracking-[0.2em] text-emerald-300">
                  {active.tagLabel}
                </span>
                <h3 className="font-display mt-4 text-2xl font-bold text-white sm:text-3xl">
                  {active.title}
                </h3>
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-2xl border border-emerald-500/15 bg-[#07120C]/60 p-4">
                    <Gauge className="h-4 w-4 text-emerald-400" />
                    <p className="font-mono-tech mt-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {t.projects.labels.capacity}
                    </p>
                    <p className="font-display mt-1 font-semibold text-white" data-testid="project-modal-capacity">
                      {active.capacity}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-emerald-500/15 bg-[#07120C]/60 p-4">
                    <MapPin className="h-4 w-4 text-emerald-400" />
                    <p className="font-mono-tech mt-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {t.projects.labels.location}
                    </p>
                    <p className="font-display mt-1 font-semibold text-white">{active.location}</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-500/15 bg-[#07120C]/60 p-4">
                    <Calendar className="h-4 w-4 text-emerald-400" />
                    <p className="font-mono-tech mt-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {t.projects.labels.year}
                    </p>
                    <p className="font-display mt-1 font-semibold text-white">{active.year}</p>
                  </div>
                </div>
                <p className="mt-6 text-sm leading-relaxed text-slate-300 sm:text-base">
                  {active.scope}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Projects;
